<template>
  <div class="about-page">
    <!-- Hero Section -->
    <section class="about-hero">
      <div class="hero-bg"></div>
      <div class="hero-content">
        <h1 class="hero-title">关于我们</h1>
      </div>
    </section>

    <!-- Brand Videos Section -->
    <section class="brand-videos">
      <div class="container">
        <h2 class="section-title">品牌视频</h2>
        
        <!-- 加载状态 -->
        <div v-if="videoLoading" class="videos-loading">
          <div class="loading-spinner"></div>
          <p>加载视频中...</p>
        </div>
        
        <!-- 视频列表 -->
        <div v-else-if="videos.length > 0" class="videos-wrapper">
          <!-- Video Controls - 在视频列表上方 -->
          <div class="video-controls">
            <button class="control-btn prev" @click="scrollVideos('prev')" :disabled="!canScrollPrev">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button class="control-btn next" @click="scrollVideos('next')" :disabled="!canScrollNext">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          
          <!-- Video List -->
          <div 
            class="videos-container" 
            ref="videosContainer"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
          >
            <div 
              class="videos-list" 
              :class="{ 'dragging': isDragging }" 
              ref="videosList" 
              :style="{ transform: `translateX(-${videoScrollPosition}px)` }"
            >
              <div class="video-card" v-for="video in videos" :key="video.id" @click="playVideo(video)">
                <div class="video-thumbnail">
                  <img :src="video.thumbnail" :alt="video.title" />
                  <div class="play-overlay">
                    <svg width="50" height="50" viewBox="0 0 60 60" fill="none">
                      <circle cx="30" cy="30" r="30" fill="rgba(255, 255, 255, 0.9)"/>
                      <path d="M24 20L40 30L24 40V20Z" fill="#ed1c24"/>
                    </svg>
                  </div>
                </div>
                <h3 class="video-title">{{ video.title }}</h3>
                <p class="video-desc">{{ video.description }}</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 空数据提示 -->
        <div v-else class="videos-empty">
          暂无品牌视频
        </div>
      </div>
    </section>
    
    <!-- Video Player Modal -->
    <div v-if="showVideoModal" class="video-modal" @click="closeVideoModal">
      <div class="video-modal-content" @click.stop>
        <button class="close-btn" @click="closeVideoModal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <video 
          v-if="currentPlayingVideo && currentPlayingVideo.url"
          :src="currentPlayingVideo.url" 
          controls 
          autoplay
          controlsList="nodownload"
          class="video-player"
          @ended="closeVideoModal"
        ></video>
      </div>
    </div>

    <!-- Brand Story Section -->
    <section class="brand-story">
      <div class="container">
        <h2 class="section-title">品牌故事</h2>
        
        <div class="story-content">
          <div class="story-text">
            <p class="story-paragraph">
              长江机油前身始于1998年，早期便投身于润滑油与工业油生产领域。2003年，“长江”品牌正式创立，专注民用润滑油的研发与生产，凭借优异品质，市场迅速拓展至全国17个省市，总工厂年产值巅峰时突破两亿元，迎来了第一个辉煌的发展高峰。
            </p>
            
            <p class="story-paragraph">
              2007年7月4日，一场因爆炸引发的火灾，让一切几乎付之一炬。尽管遭遇如此重大的挫折，长江品牌仍展现了惊人的韧性，在困境中坚持复工生产。但巨大的损失依然让企业陷入了长达数年的经营困境。
            </p>
            
            <p class="story-paragraph">
              2025年，长江品牌迎来了决定性的转折点——正式依托中国石油强大的研发资源与技术实力。这次深度融合，为品牌注入了全新的科技能量与发展动力，开启了涅槃重生的新篇章。
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Partners Section (Same as Home) -->
    <section class="partners-section">
      <div class="container">
        <h2 class="section-title">合作伙伴</h2>
        
        <div class="partners-header">
          <div class="partners-tabs">
            <button 
              v-for="category in partnerCategories"
              :key="category.dictCode"
              class="tab-btn"
              :class="{ active: partnerTab === category.dictValue }"
              @click="switchPartnerCategory(category.dictValue)"
            >
              {{ category.dictLabel }}
            </button>
          </div>
          
          <!-- 左右导航按钮：仅在高校机构tab且有数据时显示 -->
          <div class="partners-arrows" v-if="partnerTab === '2' && partnersList.length > 0">
            <button class="arrow-btn prev" @click="prevPartner">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button class="arrow-btn next" @click="nextPartner">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
        
        <!-- 专家团内容 (dictValue === '1') -->
        <div class="partners-content" v-if="partnerTab === '1' && !partnersLoading">
          <div 
            class="team-cards" 
            :class="{ 'single-card': partnersList.length === 1 }"
            v-if="partnersList.length > 0"
          >
            <!-- 显示所有专家 -->
            <div 
              v-for="(partner, index) in partnersList"
              :key="partner.id"
              class="team-card"
              :class="{ 
                featured: index % 2 === 1,
                'single-team-card': partnersList.length === 1
              }"
            >
              <div class="team-card-inner">
                <div class="team-image">
                  <img :src="partner.image" :alt="partner.name" />
                  <div class="team-label">专家</div>
                </div>
                <div :class="index % 2 === 1 ? 'team-info-wrapper' : ''">
                  <div class="team-info">
                    <h3>{{ partner.name }}</h3>
                    <p class="team-subtitle">{{ partner.nameEn || 'Expert' }}</p>
                    <p class="team-desc">{{ partner.desc || '暂无介绍' }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-data">
            陆续上线中，敬请期待
          </div>
        </div>
        
        <!-- 高校/机构内容 (dictValue === '2') -->
        <div class="partners-content" v-if="partnerTab === '2' && !partnersLoading">
          <div class="institution-content" v-if="partnersList.length > 0 && partnersList[currentPartnerIndex]">
            <div class="institution-left">
              <img :src="partnersList[currentPartnerIndex].image" :alt="partnersList[currentPartnerIndex].name" />
              <div class="institution-label">机构</div>
            </div>
            <div class="institution-right">
              <h3>{{ partnersList[currentPartnerIndex].name }}</h3>
              <p class="institution-location">{{ partnersList[currentPartnerIndex].nameEn || '' }}</p>
              <p class="institution-desc">{{ partnersList[currentPartnerIndex].desc || '暂无介绍' }}</p>
              <div class="institution-dots">
                <span 
                  v-for="(item, idx) in partnersList" 
                  :key="item.id"
                  class="dot"
                  :class="{ active: idx === currentPartnerIndex }"
                  @click="currentPartnerIndex = idx"
                ></span>
              </div>
            </div>
          </div>
          <div v-else class="no-data">
            陆续上线中，敬请期待
          </div>
        </div>
        
        <!-- 加载中状态 -->
        <div class="partners-content" v-if="partnersLoading">
          <div class="loading-partners">
            加载中...
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'About',
  data() {
    return {
      partnerTab: '1', // 默认选中专家团（使用dictValue）
      videoScrollPosition: 0,
      maxVideoScroll: 0,
      currentPartnerIndex: 0,
      // 合作伙伴相关数据
      partnerCategories: [], // 合作伙伴分类列表
      partnersList: [], // 当前分类的合作伙伴列表
      partnersLoading: false,
      // 视频相关数据
      videos: [],
      videoLoading: false,
      showVideoModal: false,
      currentPlayingVideo: null,
      // 触摸拖动相关
      touchStartX: 0,
      touchStartScrollPosition: 0,
      isDragging: false
    };
  },
  computed: {
    canScrollPrev() {
      return this.videoScrollPosition > 0;
    },
    canScrollNext() {
      return this.videoScrollPosition < this.maxVideoScroll;
    }
  },
  async mounted() {
    // Scroll to top when entering the page
    window.scrollTo(0, 0);
    // Calculate max scroll for videos
    this.calculateMaxScroll();
    window.addEventListener('resize', this.calculateMaxScroll);
    // 并行获取视频和合作伙伴数据
    await Promise.all([
      this.fetchVideoList(),
      this.fetchPartnerCategories()
    ]);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateMaxScroll);
    // 确保恢复页面滚动
    document.body.style.overflow = '';
  },
  methods: {
    scrollVideos(direction) {
      const scrollAmount = 400; // Scroll amount in pixels
      
      if (direction === 'prev') {
        this.videoScrollPosition = Math.max(0, this.videoScrollPosition - scrollAmount);
      } else {
        this.videoScrollPosition = Math.min(this.maxVideoScroll, this.videoScrollPosition + scrollAmount);
      }
    },
    calculateMaxScroll() {
      if (this.$refs.videosContainer && this.$refs.videosList) {
        const container = this.$refs.videosContainer;
        const list = this.$refs.videosList;
        const containerWidth = container.clientWidth;
        const listWidth = list.scrollWidth;
        const oldMaxScroll = this.maxVideoScroll;
        this.maxVideoScroll = Math.max(0, listWidth - containerWidth);
        console.log('Video scroll calculated:', {
          videosCount: this.videos.length,
          containerWidth,
          listWidth,
          oldMaxScroll,
          newMaxVideoScroll: this.maxVideoScroll,
          canScrollNext: this.maxVideoScroll > 0,
          currentPosition: this.videoScrollPosition
        });
      } else {
        console.warn('Cannot calculate scroll: refs not ready', {
          hasContainer: !!this.$refs.videosContainer,
          hasList: !!this.$refs.videosList,
          videosCount: this.videos.length
        });
      }
    },
    playVideo(video) {
      // 如果正在拖动，不播放视频
      if (this.isDragging) {
        return;
      }
      
      console.log('Playing video:', video.title);
      // 在弹窗中播放视频
      if (video.url) {
        this.currentPlayingVideo = video;
        this.showVideoModal = true;
        // 禁止页面滚动
        document.body.style.overflow = 'hidden';
      }
    },
    
    closeVideoModal() {
      this.showVideoModal = false;
      this.currentPlayingVideo = null;
      // 恢复页面滚动
      document.body.style.overflow = '';
    },
    
    // 触摸开始
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartScrollPosition = this.videoScrollPosition;
      this.isDragging = false; // 初始时不算拖动
    },
    
    // 触摸移动
    handleTouchMove(e) {
      const touchCurrentX = e.touches[0].clientX;
      const diff = this.touchStartX - touchCurrentX;
      
      // 如果移动距离超过5px，才算作拖动
      if (Math.abs(diff) > 5) {
        this.isDragging = true;
        const newPosition = this.touchStartScrollPosition + diff;
        
        // 限制滚动范围
        this.videoScrollPosition = Math.max(0, Math.min(this.maxVideoScroll, newPosition));
        
        // 防止页面滚动
        e.preventDefault();
      }
    },
    
    // 触摸结束
    handleTouchEnd() {
      // 延迟重置isDragging，防止触发点击事件
      setTimeout(() => {
        this.isDragging = false;
      }, 100);
    },
    
    // 获取品牌视频列表
    async fetchVideoList() {
      try {
        this.videoLoading = true;
        const res = await this.$api.video.getVideoList();
        console.log('品牌视频列表响应:', res);
        
        if (res.code === 200 && res.data) {
          // 处理视频数据
          this.videos = res.data.map(item => {
            return {
              id: item.id,
              title: item.title || '品牌视频',
              description: item.des || '',
              url: item.video || '', // 视频URL不需要拼接域名，直接使用
              thumbnail: item.cover ? this.getMediaUrl(item.cover) : require('../assets/images/media-center.png'), // 使用cover字段作为封面图，需要拼接域名
              tag: item.tag || '',
              sort: item.sort || 0
            };
          });
          
          // 按sort字段排序
          this.videos.sort((a, b) => (a.sort || 0) - (b.sort || 0));
          
          // 重新计算滚动范围 - 多次尝试确保获取正确的尺寸
          this.$nextTick(() => {
            // 立即计算一次
            this.calculateMaxScroll();
            // 延迟后再计算一次（等待图片加载）
            setTimeout(() => {
              this.calculateMaxScroll();
            }, 100);
            // 再延迟一次，确保图片完全加载
            setTimeout(() => {
              this.calculateMaxScroll();
            }, 500);
          });
        }
      } catch (error) {
        console.error('获取品牌视频列表失败:', error);
        this.videos = [];
      } finally {
        this.videoLoading = false;
      }
    },
    
    // 获取媒体(图片)完整URL - 与BannerSlider组件保持一致
    getMediaUrl(url) {
      // 与BannerSlider组件保持完全一致
      return process.env.VUE_APP_IMAGE_URL + url;
    },
    
    // 获取合作伙伴分类
    async fetchPartnerCategories() {
      try {
        const res = await this.$api.dict.getFriendsCategory();
        if (res.code === 200 && res.data) {
          this.partnerCategories = res.data;
          // 如果有分类，默认选中第一个
          if (this.partnerCategories.length > 0) {
            this.partnerTab = this.partnerCategories[0].dictValue;
            await this.fetchPartnersList(this.partnerTab);
          }
        }
      } catch (error) {
        console.error('获取合作伙伴分类失败:', error);
      }
    },
    
    // 获取合作伙伴列表
    async fetchPartnersList(category) {
      try {
        this.partnersLoading = true;
        const res = await this.$api.friends.getFriendsByCategory(category);
        if (res.code === 200 && res.data) {
          this.partnersList = res.data.map(item => ({
            id: item.id,
            name: item.friendsName,
            nameEn: item.friendsNameEn,
            desc: item.friendsDesc,
            image: item.friendsUrl ? this.getMediaUrl(item.friendsUrl) : require('../assets/images/partners1.png'),
            sort: item.sort
          }));
          // 重置索引
          this.currentPartnerIndex = 0;
        }
      } catch (error) {
        console.error('获取合作伙伴列表失败:', error);
        this.partnersList = [];
      } finally {
        this.partnersLoading = false;
      }
    },
    
    // 切换合作伙伴分类
    async switchPartnerCategory(categoryValue) {
      if (this.partnerTab !== categoryValue) {
        this.partnerTab = categoryValue;
        await this.fetchPartnersList(categoryValue);
      }
    },
    
    prevPartner() {
      // 切换到上一个合作伙伴
      this.currentPartnerIndex = Math.max(0, this.currentPartnerIndex - 1);
    },
    
    nextPartner() {
      // 切换到下一个合作伙伴
      if (this.partnersList.length > 0) {
        const maxIndex = this.partnerTab === '1' ? 
          Math.floor((this.partnersList.length - 1) / 2) : 
          this.partnersList.length - 1;
        this.currentPartnerIndex = Math.min(maxIndex, this.currentPartnerIndex + 1);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.about-page {
  min-height: 100vh;
  background: #fff;
}

/* Hero Section */
.about-hero {
  position: relative;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-top: 80px;

  @media (max-width: 768px) {
    height: 250px;
    margin-top: 60px;
  }

  .hero-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('../assets/images/about-header.png') center/cover no-repeat;
    filter: brightness(0.6);
  }

  .hero-content {
    position: relative;
    z-index: 2;
    text-align: center;
  }

  .hero-title {
    font-size: 48px;
    font-weight: bold;
    color: #fff;
    margin: 0;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

    @media (max-width: 768px) {
      font-size: 32px;
    }
  }
}

/* Brand Videos Section */
.brand-videos {
  padding: 100px 0;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 50px 0;
  }

  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 40px;

    @media (max-width: 768px) {
      padding: 0 15px;
    }
  }

  .section-title {
    font-size: 42px;
    font-weight: bold;
    color: #333;
    text-align: center;
    margin-bottom: 80px;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -20px;
      left: 50%;
      transform: translateX(-50%);
      width: 160px;
      height: 48px;
      background: url('../assets/images/title-bottom-bg.png') center/contain no-repeat;
    }

    @media (max-width: 768px) {
      font-size: 28px;
      margin-bottom: 50px;

      &::after {
        width: 120px;
        height: 36px;
        bottom: -15px;
      }
    }
  }

  .videos-wrapper {
    position: relative;
  }

  .video-controls {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-bottom: 20px;

    @media (max-width: 768px) {
      margin-bottom: 15px;
      gap: 8px;
    }

    .control-btn {
      width: 40px;
      height: 40px;
      background: #fff;
      border: 1px solid #e0e0e0;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;

      @media (max-width: 768px) {
        width: 32px;
        height: 32px;
      }
      
      &:hover:not(:disabled) {
        background: #ed1c24;
        border-color: #ed1c24;
        
        svg {
          stroke: #fff;
        }
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      svg {
        width: 18px;
        height: 18px;
        stroke: #666;
        transition: stroke 0.3s ease;

        @media (max-width: 768px) {
          width: 14px;
          height: 14px;
        }
      }
    }
  }

  .videos-container {
    overflow: hidden;
    position: relative;
    touch-action: pan-y;
    user-select: none;
    -webkit-user-select: none;
  }

  .videos-list {
    display: flex;
    gap: 30px;
    transition: transform 0.5s ease;
    padding: 10px 0;
    
    &.dragging {
      transition: none;
    }

    @media (max-width: 768px) {
      gap: 15px;
    }
  }

  .video-card {
    flex: 0 0 380px;
    cursor: pointer;
    transition: transform 0.3s ease;

    @media (max-width: 768px) {
      flex: 0 0 220px;
    }

    &:hover {
      transform: translateY(-5px);

      .play-overlay {
        opacity: 1;
      }
    }

    .video-thumbnail {
      position: relative;
      width: 100%;
      height: 280px;
      border-radius: 8px;
      overflow: hidden;
      background: #333;

      @media (max-width: 768px) {
        height: 150px;
        border-radius: 6px;
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .play-overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        opacity: 0.8;
        transition: opacity 0.3s ease;

        @media (max-width: 768px) {
          svg {
            width: 40px;
            height: 40px;
          }
        }
      }
    }

    .video-title {
      font-size: 18px;
      font-weight: 600;
      color: #333;
      margin: 20px 0 10px 0;
      line-height: 1.4;

      @media (max-width: 768px) {
        font-size: 14px;
        margin: 12px 0 8px 0;
        line-height: 1.3;
      }
    }

    .video-desc {
      font-size: 14px;
      color: #666;
      line-height: 1.6;

      @media (max-width: 768px) {
        font-size: 12px;
        line-height: 1.5;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }
  }
  
  // 视频加载状态
  .videos-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    
    .loading-spinner {
      width: 50px;
      height: 50px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #ed1c24;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 20px;
    }
    
    p {
      color: #666;
      font-size: 16px;
    }
  }
  
  // 空数据提示
  .videos-empty {
    text-align: center;
    padding: 100px 20px;
    color: #999;
    font-size: 16px;
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
}

/* Video Player Modal */
.video-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  .video-modal-content {
    position: relative;
    width: 100%;
    max-width: 1200px;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
    animation: slideUp 0.3s ease;
    
    @keyframes slideUp {
      from {
        transform: translateY(50px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
    
    .close-btn {
      position: absolute;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      background: rgba(0, 0, 0, 0.6);
      border: none;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10;
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(237, 28, 36, 0.9);
        transform: rotate(90deg);
      }
      
      svg {
        display: block;
      }
    }
    
    .video-player {
      width: 100%;
      height: auto;
      max-height: 80vh;
      display: block;
      background: #000;
      
      @media (max-width: 768px) {
        max-height: 60vh;
      }
    }
  }
  
  @media (max-width: 768px) {
    padding: 10px;
    
    .video-modal-content {
      max-width: 100%;
      
      .close-btn {
        top: 10px;
        right: 10px;
        width: 36px;
        height: 36px;
        
        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }
}

/* Brand Story Section */
.brand-story {
  position: relative;
  padding: 120px 0;
  overflow: hidden;
  background: url('../assets/images/about-story-bg.png') center/cover no-repeat;

  @media (max-width: 768px) {
    padding: 80px 0;
  }

  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 40px;
    position: relative;

    @media (max-width: 768px) {
      padding: 0 20px;
    }
  }

  .section-title {
    font-size: 42px;
    font-weight: bold;
    color: #fff;
    text-align: center;
    margin-bottom: 80px;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -20px;
      left: 50%;
      transform: translateX(-50%);
      width: 160px;
      height: 48px;
      background: url('../assets/images/title-bottom-bg.png') center/contain no-repeat;
    }

    @media (max-width: 768px) {
      font-size: 32px;
      margin-bottom: 60px;
    }
  }

  .story-content {
    max-width: 1000px;
    margin: 0 auto;
  }

  .story-text {
    .story-paragraph {
      font-size: 17px;
      line-height: 1.8;
      color: #fff;
      margin-bottom: 30px;
      text-align: justify;
      text-indent: 2em;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3); // 添加阴影提高可读性

      @media (max-width: 768px) {
        font-size: 15px;
        line-height: 1.7;
      }
    }
  }
}

/* Partners Section (Copy from Home) */
.partners-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%);

  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 40px;

    @media (max-width: 768px) {
      padding: 0 20px;
    }
  }

  .section-title {
    text-align: center;
    font-size: 42px;
    font-weight: bold;
    color: #333;
    margin-bottom: 80px;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -20px;
      left: 50%;
      transform: translateX(-50%);
      width: 160px;
      height: 48px;
      background: url('../assets/images/title-bottom-bg.png') center/contain no-repeat;
    }

    @media (max-width: 768px) {
      font-size: 32px;
      margin-bottom: 60px;
    }
  }

  .partners-header {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 60px;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 30px;
    }

    .partners-tabs {
      display: inline-flex;
      background: #f5f5f5;
      border-radius: 8px;
      overflow: hidden;
      padding: 0;

      .tab-btn {
        width: 150px;
        padding: 12px 0;
        background: transparent;
        border: none;
        font-size: 16px;
        font-weight: 500;
        color: #666;
        cursor: pointer;
        transition: all 0.3s ease;
        text-align: center;

        &:first-child {
          border-radius: 8px 0 0 8px;
          
          &.active {
            background: linear-gradient(270deg, #A31C20 0%, #DF0414 100%);
            color: #fff;
            border-radius: 8px 0 0 8px;
          }
        }
        
        &:last-child {
          border-radius: 0 8px 8px 0;
          
          &.active {
            background: linear-gradient(270deg, #A31C20 0%, #DF0414 100%);
            color: #fff;
            border-radius: 0 8px 8px 0;
          }
        }

        &:hover:not(.active) {
          background: rgba(0, 0, 0, 0.05);
        }
      }
    }

    .partners-arrows {
      position: absolute;
      top: 0;
      right: 0;
      display: flex;
      gap: 10px;

      @media (max-width: 768px) {
        position: static;
        margin-top: 20px;
      }

      .arrow-btn {
        width: 40px;
        height: 40px;
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        color: #666;

        &:hover {
          background: #ed1c24;
          border-color: #ed1c24;
          color: #fff;
        }

        &.prev svg {
          margin-right: 2px;
        }

        &.next svg {
          margin-left: 2px;
        }
      }
    }
  }

  .partners-content {
    .team-cards {
      display: flex;
      flex-wrap: wrap;
      gap: 40px;
      
      // 当只有一个卡片时，居中显示且宽度为50%
      &.single-card {
        justify-content: center;
        
        .single-team-card {
          flex: 0 0 50%;
          max-width: 600px; // 限制最大宽度
        }
      }
      
      @media (max-width: 992px) {
        flex-direction: column;
        
        &.single-card .single-team-card {
          flex: 0 0 100%; // 移动端恢复全宽
          max-width: 100%;
        }
      }

      .team-card {
        flex: 0 0 calc(50% - 20px);
        background: #fff;
        overflow: hidden;
        
        @media (max-width: 992px) {
          flex: 0 0 100%;
        }

        .team-card-inner {
          display: flex;
          height: 300px;
          align-items: stretch;
          
          @media (max-width: 768px) {
            flex-direction: column;
            height: auto;
          }
          
          // 给所有卡片的信息区域外层容器添加统一样式
          > div:not(.team-image) {
            flex: 1;
            display: flex;
            align-items: center;
          }
        }

        .team-image {
          position: relative;
          flex: 0 0 250px;
          
          @media (max-width: 768px) {
            flex: 1;
            height: 200px;
          }
          
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .team-label {
            position: absolute;
            top: 0;
            left: 0;
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            color: #fff;
            padding: 12px 28px;
            font-size: 18px;
            font-weight: 600;
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 0;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }
        }

        .team-info {
          flex: 1;
          padding: 40px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          width: 100%;
          
          @media (max-width: 768px) {
            padding: 30px;
          }
          
          h3 {
            font-size: 22px;
            font-weight: bold;
            color: #333;
            margin-bottom: 8px;
          }

          .team-subtitle {
            font-size: 14px;
            color: #999;
            margin-bottom: 15px;
          }

          .team-desc {
            font-size: 15px;
            color: #666;
            line-height: 1.6;
          }
        }

        &.featured {
          .team-info-wrapper {
            flex: 1;
            background: #ed1c24;
            padding: 40px;
            display: flex;
            align-items: center;

            @media (max-width: 768px) {
              padding: 30px;
            }

            .team-info {
              padding: 0;

              h3, .team-desc {
                color: #fff;
              }

              .team-subtitle {
                color: rgba(255, 255, 255, 0.8);
              }
            }
          }
        }
      }
    }

    .institution-content {
      display: flex;
      background: #fff;
      overflow: hidden;

      @media (max-width: 992px) {
        flex-direction: column;
      }

      .institution-left {
        position: relative;
        flex: 0 0 500px;

        @media (max-width: 992px) {
          flex: 1;
          min-height: 300px;
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .institution-label {
          position: absolute;
          top: 0;
          left: 0;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          color: #fff;
          padding: 14px 32px;
          font-size: 20px;
          font-weight: 600;
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 0;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
      }

      .institution-right {
        flex: 1;
        padding: 60px;
        background: #f8f9fa;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;

        @media (max-width: 768px) {
          padding: 40px 30px;
        }

        h3 {
          font-size: 28px;
          font-weight: bold;
          color: #333;
          margin-bottom: 10px;
        }

        .institution-location {
          font-size: 16px;
          color: #999;
          margin-bottom: 30px;
        }

        .institution-desc {
          font-size: 16px;
          color: #666;
          line-height: 1.8;
          margin-bottom: 40px;
        }

        .institution-dots {
          display: flex;
          gap: 10px;

          .dot {
            width: 10px;
            height: 10px;
            background: #ddd;
            border-radius: 50%;
            cursor: pointer;
            transition: all 0.3s ease;

            &.active {
              background: #ed1c24;
              width: 30px;
              border-radius: 5px;
            }
          }
        }
      }
    }
    
    // 加载状态和无数据状态样式
    .loading-partners,
    .no-data {
      text-align: center;
      padding: 60px 20px;
      color: #999;
      font-size: 16px;
    }
  }
}
</style>
